/*
* This code is the intellectual property of Bulb Inc. and other software publishers;
* it may not be altered, copied or disclosed without prior written approval.
* Copyright 2021, Bulb Inc. For more information, please email contact@hellobulb.com.
*/
import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit, Directive, ElementRef, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, PLATFORM_ID,
} from '@angular/core';

@Directive({
  selector: '[bulbRunInView]',
  standalone: false,
})
export class RunInViewDirective implements OnInit, AfterViewInit, OnDestroy {
  @Input() public removeListenersAfterLoad: boolean = true;

  @Output() public enterView: EventEmitter<any> = new EventEmitter();

  private intersectionObserver?: IntersectionObserver;

  constructor(
    private element: ElementRef,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) { }

  public ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) {
      // eslint-disable-next-line no-console
      console.warn('RunInViewDirective.ngOnInit: Requires IntersectionObserver!');
    }
  }

  public ngAfterViewInit() {
    this.registerIntersectionObserver();
    if (this.intersectionObserver && this.element.nativeElement) {
      this.intersectionObserver.observe(<Element>(this.element.nativeElement));
    }
  }

  public ngOnDestroy() {
    this.removeListeners();
  }

  private registerIntersectionObserver(): void {
    if (this.intersectionObserver) {
      return;
    }
    this.intersectionObserver = new IntersectionObserver((entries) => {
      this.checkForIntersection(entries);
    }, {});
  }

  private checkForIntersection = (entries: Array<IntersectionObserverEntry>) => {
    entries.forEach((entry: IntersectionObserverEntry) => {
      if (this.checkIfIntersecting(entry)) {
        this.load();
        if (this.intersectionObserver && this.element.nativeElement) {
          this.intersectionObserver.unobserve(<Element>(this.element.nativeElement));
        }
      }
    });
  };

  private checkIfIntersecting(entry: IntersectionObserverEntry) {
    return entry?.isIntersecting && entry?.target === this.element.nativeElement;
  }

  private load(): void {
    if (this.removeListenersAfterLoad) {
      this.removeListeners();
    }
    this.enterView.emit();
  }

  private removeListeners() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }
}
